import { useRef, useState, useEffect } from "react";
import { useNotify } from "react-admin";
import { useController } from "react-hook-form";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieIcon from "@mui/icons-material/Movie";
import { authUpload } from "../api/dataProvider";
import { API_BASE } from "../api/config";
import { useCommonLabels } from "../i18n/hooks";

export const isFileUid = (value: string) =>
  /^[0-9a-fA-F]{32}$/.test(value) ||
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    value,
  );

export const fileSrc = (value: string | null | undefined): string | null => {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (isFileUid(value)) return `${API_BASE}/api/file/get/${value}`;
  return null;
};

const uploadFile = (file: File): Promise<string> => authUpload(file);

type FileUidInputProps = {
  source: string;
  label?: string;
  accept?: string;
  helperText?: string;
  required?: boolean;
  validate?: (
    value: unknown,
    allValues: Record<string, unknown>,
  ) => string | undefined;
  onValueChange?: (value: string) => void;
  kind?: "image" | "audio" | "video" | "media" | "any";
};

export const FileUidInput = ({
  source,
  label,
  accept,
  helperText,
  kind = "image",
  required = false,
  validate,
  onValueChange,
}: FileUidInputProps) => {
  const c = useCommonLabels();
  const notify = useNotify();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [mediaPreviewMode, setMediaPreviewMode] = useState<"video" | "audio">("video");

  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name: source,
    defaultValue: "",
    rules:
      required || validate
        ? {
            ...(required ? { required: c.requiredField ?? "Required" } : {}),
            ...(validate ? { validate } : {}),
          }
        : undefined,
  });

  const src = fileSrc(typeof value === "string" ? value : null);
  useEffect(() => {
    setMediaPreviewMode("video");
  }, [src, kind]);
  const resolvedAccept =
    accept ??
    (kind === "audio"
      ? "audio/*"
      : kind === "video"
        ? "video/*"
        : kind === "media"
          ? "video/*,audio/*"
        : kind === "any"
          ? "image/*,video/*,audio/*"
          : "image/*");

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const uid = await uploadFile(file);
      onChange(uid, { shouldDirty: true });
      onValueChange?.(uid);
      notify(c.fileUploaded, { type: "info" });
    } catch (exc: unknown) {
      const message = exc instanceof Error ? exc.message : String(exc);
      notify(`${c.uploadError}: ${message}`, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = () => {
    onChange("", { shouldDirty: true });
    onValueChange?.("");
  };

  const preview = (() => {
    if (!src) return null;
    if (kind === "video" || (kind === "media" && mediaPreviewMode === "video")) {
      return (
        <Box
          component="video"
          src={src}
          controls
          onError={() => {
            if (kind === "media") setMediaPreviewMode("audio");
          }}
          sx={{
            width: 160,
            height: 96,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            objectFit: "cover",
            flexShrink: 0,
            backgroundColor: "grey.900",
          }}
        />
      );
    }
    if (kind === "audio" || (kind === "media" && mediaPreviewMode === "audio")) {
      return (
        <Box
          component="audio"
          src={src}
          controls
          sx={{
            width: 220,
            flexShrink: 0,
          }}
        />
      );
    }
    return (
      <Box
        sx={{
          position: "relative",
          width: 96,
          height: 96,
          borderRadius: 1,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={src}
          alt={label ?? source}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.opacity = "0.25";
          }}
        />
      </Box>
    );
  })();

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, width: "100%" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label ?? source}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        {preview ?? (
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              flexShrink: 0,
            }}
          >
            {kind === "video" || kind === "media" ? (
              <MovieIcon sx={{ fontSize: 32, opacity: 0.5 }} />
            ) : kind === "audio" ? (
              <AudiotrackIcon sx={{ fontSize: 32, opacity: 0.5 }} />
            ) : (
              <Typography variant="caption">{c.noFile}</Typography>
            )}
          </Box>
        )}

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              {busy ? c.uploading : src ? c.replace : c.upload}
            </Button>
            {value ? (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleRemove}
              >
                {c.delete}
              </Button>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
            {value ? String(value) : helperText ?? c.fileUploadHint}
          </Typography>
          {error ? (
            <Typography variant="caption" color="error">
              {error.message}
            </Typography>
          ) : null}
        </Stack>
        <input
          ref={fileInput}
          type="file"
          accept={resolvedAccept}
          hidden
          onChange={handleFile}
        />
      </Stack>
    </Paper>
  );
};

export const FilePreviewLink = ({ value }: { value?: string | null }) => {
  const src = fileSrc(value);
  if (!value) return <>—</>;
  if (!src) return <>{value}</>;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      {value}
    </a>
  );
};
